import { Injectable } from '@angular/core';
import { OrigamiHttpResponse, OrigamiResponse, ServiceBase } from './base/service.base';
import { Observable } from 'rxjs';
import { SimpleTopic, Topic, TopicField } from '@app/origami-api/utils/topic-utils';
import {
  FolderManagementServiceModel,
  FolderManagementServiceOptions,
} from '@app/origami-api/services/folder-management.service';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { EssentialDocumentsStatusesEnum } from '@app/origami-api/constants/statuses';
import { ErrorRecord } from '@app/origami-api/utils/error-utils';
import ReplaceAttachmentURL = FolderManagementServiceModel.ReplaceAttachmentURL;
import { TimeZoneService } from '@app/services/time-zone.service';
import { tap } from 'rxjs/operators';
import { Converter } from '@app/shared/utils/converter';
import { DateFormatStore } from '@app/session/date-format-store';
import { HttpXhrBackend } from '@angular/common/http';
import { CASAuthRequestProxy } from '@app/origami-api/services/base/request-proxy/cas-auth.request-proxy.class';

export namespace SSUEssentialDocument {
  import GetDocumentsByViewParams = FolderManagementServiceOptions.GetDocumentsByViewParams;

  export interface DeleteDocumentsOptions {
    amendmentProfileId: number;
    documents: { ChildTopicId: number }[];
    essentialFor: number;
    essentialForId: string;
    removalReason: string;
    siteId: number;
  }

  export interface GetEssentialDocumentProfileOptions {
    siteId: number;
    topicId: number;
    getDocumentParams?: {
      IncludeTopicAcls?: boolean;
      IncludeWorkflow?: boolean;
      IncludeAudit?: boolean;
      IncludeQueryTickets?: boolean;
      IncludeSuggestedDocumentTypes?: boolean; // should not be used, we have a separate API for that. it drastically increases the API execution time
      QueryTicketCreatedBy?: string;
      QueryEntityId?: number;
      ExcludeCustomForm?: boolean;
      QueryType?: number;
      IncludePostedDeviceInfo?: boolean;
      FieldListing?: string;
    };
  }

  export interface EssentialDocumentsProgress {
    EntityId: string;
    EntityName: string;
    EntityType: number;
    MissingDocuments: EssentialMissingDocuments[];
    ProgressPercent: number;
    RegProgressPercent: number;
  }

  export interface EssentialMissingDocuments {
    ContactEmail: string;
    ContactId: number;
    ContactName: string;
    ContactTypeId: number;
    ContactTypeName: string;
    DocumentTypeId: number;
    DocumentTypeName: string;
    LanguageCode: string;
  }

  export interface EssentialDocuments {
    ApprovedDate: string;
    ContactFullName: string;
    ContactId: number;
    ContactName: string;
    DocumentProfile: EssentialDocumentProfile;
    DocumentTitle: string;
    DocumentType: EssentialDocumentType;
    DocumentTypeName: string;
    LanguageCode: string;
    ReceivedDate: string;
    RelatedContactTypeId: number;
    RelatedContactTypeName: string;
    RequiredBy: number;
    RequiredById: string;
    RequiredById2: number;
    RequiredByName: any;
  }

  export interface EssentialDocumentType {
    CreatedBy: number;
    CreatedDate: string;
    LockedBy: string;
    ParentId: number;
    SiteId: number;
    TopicFields: TopicField[];
    TopicId: number;
    TopicName: string;
    TopicTypeId: number;
  }

  export interface EssentialDocumentProfile extends FolderManagementServiceModel.Document, Topic {
    DocReplaceOption: number;
    FileName: string;
    IsDocumentCollected: boolean;
    SSUDocumentTopicId: number;
    SSUMetaDataFields: TopicField[];
    StartupHistory: SSUHistoryModel[];
  }

  export interface SSUHistoryModel {
    CreatedBy: string;
    RecordDate: string;
    EventType: number;
  }

  export enum EssentialFor {
    providedByCountry = 1,
    providedByIrb = 2,
    providedByDataRoom = 3,
    providedByInvestigativeSite = 4,
  }

  export interface ProcessUploadedDocuments {
    siteId: number;
    uploadedDocuments: ImportSSUDocumentsRequest;
  }

  export interface ImportSSUDocumentsRequest {
    EssentialFor: EssentialFor;
    EssentialForId: string | number;
    AmendmentProfileId: number;
    UploadedFiles: {
      DocReplaceOption?: ImportSSUDocReplaceOption;
      FileToken: string;
      RelativeFilePath: string;
    }[];
    UnpackArchive: boolean;
    ConvertToPDF: boolean;
    MetaData: TopicField[];
  }

  export enum ImportSSUDocReplaceOption {
    ReplaceDocument = 1,
    SkipReplace,
    RemoveDocumentType,
  }

  export interface ProcessUploadedDocumentsResult {
    ErrorCode: number;
    Result: string;
  }

  export interface CreateEssentialDocument {
    siteId: number;
    amendmentProfileId: any;
    document: {
      TopicFields: TopicField[];
      SSUMetaDataFields?: TopicField[];
      FileToken?: string;
      TopicName?: string;
      DocumentUrl?: string;
    };
  }

  export interface ReplaceStartUpDocumentAttachment {
    siteId: number;
    docId: number;
    replaceReason: string;
    newAttachmentTocken?: string;
    newDocumentUrl?: string;
    amendmentProfileId: number;
  }

  export interface CreateEssentialDocumentResponse {
    DocumentDate: any;
    DuplicateDocumentId: number;
    ErrorCode: number;
    ErrorMessage: any;
    ErrorRecords: any[];
    FieldId: number;
    InvalidFolders: [];
    TopicId: number;
    TopicName: any;
    UploadedFileName: any;
  }

  export interface GetSSUDocumentsViews {
    ViewFilterDisplayName?: string;
    ViewFilterId?: string;
    ViewFilterIdAdditional?: string;
    ViewMode?: number;
    EntitiesCount?: number;
  }

  export type GetDocumentByViewOptions = GetDocumentsByViewParams;

  export interface GetDocumentByView {
    PageNumber: number;
    TopicList: EssentialDocumentType[];
    TotalRowCount: number;
  }

  export enum DocumentsProvidedBy {
    byCountry = 1,
    byIrb = 2,
    byDataRoom = 3,
    byInvestigativeSite = 4,
  }

  export enum StartUpDocumentReplaceOptions {
    NotInitialized = 0,
    ReplaceDocument = 1,
    SkipReplace = 2,
    RemoveDocumentType = 3,
  }

  export interface EditEssentialDocumentOptions {
    siteId: number;
    amendmentProfileId: any;
    document: {
      DocReplaceOption: StartUpDocumentReplaceOptions;
      SSUMetaDataFields?: TopicField[];
      TopicFields: TopicField[];
      TopicId?: number;
      TopicName: string;
    };
  }

  export enum EssentialDocumentApproveMode {
    None = 0,
    QCApproval = 1,
    RegulatoryApproval = 2,
    SponsorApproval = 3,
  }

  export interface ChangesEssDocStatusOptions {
    siteId: number;
    essentialFor: EssentialFor;
    essentialForId: number | string;
    topicIds: number[];
    status: EssentialDocumentsStatusesEnum;
    approvalMode: EssentialDocumentApproveMode;
    comments: string;
    amendmentProfileId: number;
  }

  export interface GetEssentialDocumentsStatusOptions {
    siteId: number;
    essentialFor: number;
    essentialForId: string | number;
    amendmentProfileId?: number;
    returnMissingDocuments: boolean;
  }

  export interface GetEssentialDocumentsStatus {
    EntityId: string;
    EntityName: string;
    EntityType: number;
    MissingDocuments: MissingDocumentsInfo[];
    ProgressPercent: number;
    RegProgressPercent: number;
    SponsorApprovedCount: number;
    SponsorUnderReviewCount: number;
  }

  export interface MissingDocumentsInfo {
    ContactEmail: string;
    ContactId: number;
    ContactName: string;
    ContactTypeId: number;
    ContactTypeName: string;
    DocumentTypeId: number;
    DocumentTypeName: string;
    LanguageCode: string;
  }

  export interface EssDocumentProfile {
    CreatedByName: string;
    // CreatedDateTime: string; // not used?
    DocumentIndexList: any;
    FileName: string;
    FileSize: number;
    InheritFolderSecurity: false;
    LastUpdatedByName: string;
    // LastUpdatedDateTime: string; // not used?
    Topic: FolderManagementServiceModel.Document;
    TopicACLs: any;
  }

  export interface RestoreEssentialDocumentsOptions {
    siteId: number;
    documents: RestoreEssentialDocumentData[];
  }

  export interface RestoreEssentialDocumentsResponse {
    AdditionalInformation?: TopicField[]; // only for ErrorCode.ESSENTIAL_DOCS_ALREADY_HAS_PROFILE
    ErrorCode: ErrorCode;
    ObjectId: number;
    ObjectName: string;
    ObjectType: string;
    RelatedObjectId: number;
    RelatedObjectName: string;
    RelatedObjectType: string;
    Severity: number;
  }

  export interface RestoreEssentialDocumentData {
    TopicId: number;
    TopicName: string;
    KeepStartupReviewData: boolean;

    DocReplaceOption?: StartUpDocumentReplaceOptions;
    EssentialFor?: number;
    EssentialForId?: string | number;
    AmendmentProfileId?: number;
    EssentialDocument?: SimpleTopic;
  }

  export interface AddDocumentsErrors {
    DocumentDate: string | null;
    DuplicateDocumentId: number;
    ErrorCode: number;
    ErrorMessage: string | null;
    ErrorRecords: ErrorRecord[];
    FieldId: number;
    InvalidFolders: [];
    TopicId: number;
    TopicName: string | null;
    UploadedFileName: string | null;
  }
}

@Injectable()
export class EssentialDocumentsService extends ServiceBase {
  serviceUrl = 'EssentialDocumentsService.svc';
  constructor(
    backend: HttpXhrBackend,
    requestProxy: CASAuthRequestProxy,
    private timeZoneService: TimeZoneService,
  ) {
    super(backend, requestProxy);
  }

  public saveEssentialDocumentsState(body): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/SaveEssentialDocumentsState', body, null, null, true);
  }

  public editEssentialDocument(
    body: SSUEssentialDocument.EditEssentialDocumentOptions,
    silentErrorCodes: ErrorCode[] = [],
  ): Observable<OrigamiResponse<ReplaceAttachmentURL | SSUEssentialDocument.AddDocumentsErrors>> {
    return this.origamiPost('/EditEssentialDocument', body, null, silentErrorCodes, true);
  }

  public changeEssentialDocumentStatus(
    body: SSUEssentialDocument.ChangesEssDocStatusOptions,
    silentErrorCodes: ErrorCode[] = [],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ChangeEssentialDocumentStatus', body, null, silentErrorCodes, true);
  }

  public getEssentialDocumentsStatus(
    body: SSUEssentialDocument.GetEssentialDocumentsStatusOptions,
    silentErrorCodes: ErrorCode[] = [],
  ): Observable<OrigamiResponse<SSUEssentialDocument.GetEssentialDocumentsStatus[]>> {
    return this.origamiPost('/GetEssentialDocumentsStatus', body, null, silentErrorCodes, true);
  }

  public deleteEssentialDocuments(body: SSUEssentialDocument.DeleteDocumentsOptions): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/DeleteEssentialDocuments', body, null, null, true);
  }

  public getEssentialDocumentProfile(
    body: SSUEssentialDocument.GetEssentialDocumentProfileOptions,
    silentErrorCodes = null,
  ): Observable<OrigamiResponse<SSUEssentialDocument.EssDocumentProfile>> {
    return this.origamiPost('/GetEssentialDocumentProfile', body, null, silentErrorCodes, true).pipe(
      tap((res) => {
        if (res.Success) {
          const result: FolderManagementServiceModel.Document = res.Result.Topic;
          /*result.AuditHistory?.forEach((history) => {
            history.CreatedDateConverted = this.formatDate(history.CreatedDate);
          });*/
          result?.QueryTickets?.forEach((queryTicket) => {
            queryTicket.CreatedDateString = this.formatDate(queryTicket.CreatedDate);
            queryTicket.LastUpdatedDateString = this.formatDate(queryTicket.LastUpdatedDate);
            queryTicket.QueryTicketActivities.forEach((activity) => {
              activity.CreatedDateString = this.formatDate(activity.CreatedDate);
            });
          });
          /*if (result.PostedDeviceInfo?.VerificationDate) {
            result.PostedDeviceInfo.VerificationDate = this.formatDate(result.PostedDeviceInfo?.VerificationDate);
          }*/
        }
      }),
    );
  }

  private formatDate(dateString: string): string {
    if (!dateString) {
      return null;
    }
    const date = Converter.DateAndTime.fromCSharpString(dateString);
    return this.timeZoneService.formatDate(date, DateFormatStore.luxonDateTimeFormatWithTime);
  }

  public createEssentialDocument(
    body: SSUEssentialDocument.CreateEssentialDocument,
    silentErrorCodes = null,
  ): Observable<
    OrigamiResponse<SSUEssentialDocument.CreateEssentialDocumentResponse | SSUEssentialDocument.AddDocumentsErrors>
  > {
    return this.origamiPost('/CreateEssentialDocument', body, null, silentErrorCodes, true);
  }

  public replaceStartUpDocumentAttachment(
    body: SSUEssentialDocument.ReplaceStartUpDocumentAttachment,
  ): Observable<OrigamiResponse<SSUEssentialDocument.CreateEssentialDocumentResponse>> {
    return this.origamiPost('/ReplaceStartUpDocumentAttachment', body, null, null, true);
  }

  public processUploadedDocuments(
    body: SSUEssentialDocument.ProcessUploadedDocuments,
    silentErrors?: ErrorCode[],
  ): Observable<OrigamiResponse<SSUEssentialDocument.ProcessUploadedDocumentsResult>> {
    return this.origamiPost('/ProcessUploadedDocuments', body, null, silentErrors, true);
  }

  public restoreEssentialDocuments(
    body: SSUEssentialDocument.RestoreEssentialDocumentsOptions,
    silentErrors?: ErrorCode[],
  ): Observable<OrigamiResponse<SSUEssentialDocument.RestoreEssentialDocumentsResponse[]>> {
    return this.origamiPost('/RestoreEssentialDocuments', body, null, silentErrors, true);
  }
}
